import React from 'react';
import { Table } from '@tanstack/react-table';
import {
  TableSection,
  EmptyState,
  ReactTable,
  ReactTableHeader,
  ReactTableHeaderRow,
  ReactTableHeaderCell,
  ReactTableBody,
  ReactTableRow,
  ReactTableCell,
} from '../PersonalBusinessReviewView.styles';
import { DataEntryRowDataType } from '../types';

interface PersonalBusinessReviewTableProps {
  table: Table<DataEntryRowDataType>;
  rowData: DataEntryRowDataType[];
  pendingClassifications: Set<string>;
  onRowClick: (row: DataEntryRowDataType) => void;
  searchTerm?: string | null;
}

export const PersonalBusinessReviewTable: React.FC<PersonalBusinessReviewTableProps> = ({
  table,
  rowData,
  pendingClassifications,
  onRowClick,
  searchTerm,
}) => {
  const emptyStateMessage = searchTerm
    ? `No transactions found matching "${searchTerm}"`
    : 'No unclassified transactions found';

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  if (rowData.length === 0) {
    return (
      <TableSection>
        <EmptyState>{emptyStateMessage}</EmptyState>
      </TableSection>
    );
  }

  return (
    <TableSection>
      <ReactTable>
        <ReactTableHeader>
          {headerGroups.map(headerGroup => (
            <ReactTableHeaderRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <ReactTableHeaderCell
                  key={header.id}
                  width={header.getSize()}
                  canSort={header.column.getCanSort()}
                  data-sort={header.column.getIsSorted() || undefined}
                  onClick={header.column.getToggleSortingHandler()}
                  align={header.id === 'amount' ? 'right' : 'left'}
                >
                  {header.isPlaceholder
                    ? null
                    : typeof header.column.columnDef.header === 'function'
                    ? header.column.columnDef.header(header.getContext())
                    : header.column.columnDef.header}
                </ReactTableHeaderCell>
              ))}
            </ReactTableHeaderRow>
          ))}
        </ReactTableHeader>
        <ReactTableBody>
          {rows.map(row => (
            <ReactTableRow
              key={row.id}
              onClick={() => onRowClick(row.original as DataEntryRowDataType)}
              isSelected={row.getIsSelected()}
              isPending={pendingClassifications.has(row.original.id)}
            >
              {row.getVisibleCells().map(cell => (
                <ReactTableCell
                  key={cell.id}
                  align={cell.column.id === 'amount' ? 'right' : 'left'}
                  width={cell.column.columnDef.size}
                >
                  {typeof cell.column.columnDef.cell === 'function'
                    ? cell.column.columnDef.cell(cell.getContext())
                    : cell.getValue()}
                </ReactTableCell>
              ))}
            </ReactTableRow>
          ))}
        </ReactTableBody>
      </ReactTable>
    </TableSection>
  );
};
